import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getHomilyArchiveList, getHomilyArchiveDetail } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/lib/t";
import { useLang } from "@/context/LangContext";

/**
 * Public read-only archive of past Homily Prep digests.
 *
 * Two exports:
 *   - HomilyArchiveList  ─── /resources/homily-digest
 *   - HomilyArchiveDetail ── /resources/homily-digest/:weekKey
 *
 * The stored HTML is sanitized server-side (open pixel + tracking-click
 * wrappers stripped) before we render it, so archive views don't inflate
 * email analytics or dead-end on ancient tracked send_ids.
 */

export function HomilyArchiveList() {
  const t = useT();
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getHomilyArchiveList(lang)
      .then((r) => setItems(r.weeks || []))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <div data-testid="homily-archive-list">
      <section className="bg-ccc-plum text-ccc-paper py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="eyebrow eyebrow-light mb-3">{t("homily_archive.eyebrow")}</div>
          <h1 className="font-serif text-5xl mb-4">{t("homily_archive.title")}</h1>
          <p className="text-ccc-paper/85 max-w-2xl" style={{ fontSize: "18px", lineHeight: 1.7 }}>
            {t("homily_archive.subtitle")}
          </p>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-6 py-12">
        {loading && <div className="text-ccc-muted" style={{ fontSize: "15px" }}>{t("common.loading")}</div>}
        {!loading && items.length === 0 && (
          <div className="text-ccc-muted font-serif" style={{ fontSize: "20px" }}>{t("homily_archive.empty")}</div>
        )}
        <ul className="space-y-3" data-testid="homily-archive-week-list">
          {items.map((w) => (
            <li key={w.week_key}>
              <Link
                to={`/resources/homily-digest/${w.week_key}`}
                data-testid={`homily-week-${w.week_key}`}
                className="block bg-white border border-ccc-rule p-5 hover:border-ccc-plum transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="eyebrow" style={{ fontSize: "12px" }}>{w.week_key}</div>
                    <div className="font-serif text-ccc-plum" style={{ fontSize: "20px" }}>{w.subject}</div>
                  </div>
                  <div className="text-ccc-muted shrink-0" style={{ fontSize: "13px" }}>
                    {w.bill_count} {t("homily_archive.bills_label")}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function HomilyArchiveDetail() {
  const { weekKey } = useParams();
  const t = useT();
  const { lang } = useLang();
  const [doc, setDoc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setNotFound(false);
    getHomilyArchiveDetail(weekKey, lang)
      .then(setDoc)
      .catch(() => setNotFound(true));
  }, [weekKey, lang]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <div className="font-serif text-3xl text-ccc-plum">{t("homily_archive.not_found")}</div>
        <Link to="/resources/homily-digest" className="mt-4 inline-flex items-center gap-1 text-ccc-plum underline decoration-ccc-gold underline-offset-4" style={{ fontSize: "15px" }}>
          <ArrowLeft size={14}/> {t("homily_archive.back_to_list")}
        </Link>
      </div>
    );
  }
  if (!doc) return <div className="max-w-3xl mx-auto px-6 py-24">{t("common.loading")}</div>;

  return (
    <article className="max-w-3xl mx-auto px-6 py-16" data-testid="homily-archive-detail">
      <Link
        to="/resources/homily-digest"
        className="text-ccc-plum inline-flex items-center gap-1 mb-8 underline decoration-ccc-gold underline-offset-4"
        style={{ fontSize: "15px" }}
      >
        <ArrowLeft size={14}/> {t("homily_archive.back_to_list")}
      </Link>
      <div className="eyebrow mb-2">{doc.week_key}</div>
      <h1 className="font-serif text-4xl text-ccc-plum leading-tight mb-8">{doc.subject}</h1>
      {/* Inject sanitized archived HTML. Server strips the tracking pixel
          and reverts click-wrapper links before this ever ships. */}
      <div
        className="homily-archive-body bg-white border border-ccc-rule p-6"
        data-testid="homily-archive-body"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: doc.html || "" }}
      />
    </article>
  );
}
